import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/bookings-attendance',
        destPath: '',
      },
      {
        srcPath: '/bookings-attendance',
        destPath: '',
      },
      {
        srcPath: '/_api/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
      {
        srcPath: '/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/bookings-attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/bookings/attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/bookings/bookings-attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/_api/bookings-attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/bookings/attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/bookings/attendance/v2/attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/bookings/v2/attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/bookings/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
      {
        srcPath: '/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
      {
        srcPath: '/_api/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
      {
        srcPath: '/bookings/attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/bookings-attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/_api/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
    ],
    _: [
      {
        srcPath: '/bookings-attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/_api/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
      {
        srcPath: '/bookings/attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/bookings-attendance',
        destPath: '/v2/attendance',
      },
      {
        srcPath: '/api/bookings-attendance/v2/bulk/attendance',
        destPath: '/v2/bulk/attendance',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_attendance';

/** Retrieves attendance information. */
export function getAttendance(payload: object): RequestOptionsFactory<any> {
  function __getAttendance({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.GetAttendance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/attendance/{attendanceId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'attendance.updatedDate' },
              { path: 'attendance.statusUpdatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAttendance;
}

/**
 * Sets or updates attendance information for a booking session. This
 * information is stored in an `attendance` object.
 *
 * If an `attendance` object already exists for the session, it's updated.
 * Otherwise, a new object is created.
 *
 * By default, `numberOfAttendees` is set to `1`, but you can specify a higher
 * number if multiple participants attended. Do not set `numberOfAttendees` to
 * `0` to indicate no attendance, instead specify `{"status": "NOT_ATTENDED"}`.
 *
 * Validation guidelines:
 *
 * + The call succeeds for mismatches between `numberOfAttendees`
 * and `status`. For example, make sure that your code doesn't specify
 * `{"status": "NOT_ATTENDED"}` with `{"numberOfAttendees": 5}`.
 * + The API also allows `numberOfAttendees` to exceed the booking's
 * `numberOfParticipants`. Use higher values only when scenarios like
 * walk-ins justify the exception.
 */
export function setAttendance(payload: object): RequestOptionsFactory<any> {
  function __setAttendance({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'attendance.updatedDate' },
          { path: 'attendance.statusUpdatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.SetAttendance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/attendance/set',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'attendance.updatedDate' },
              { path: 'attendance.statusUpdatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setAttendance;
}

/**
 * Sets or updates attendance information for multiple booking sessions.
 *
 *
 * Refer to Set Attendance for detailed behavior of individual attendance
 * entries.
 *
 * The call fails entirely if any entry in `attendanceDetails` is missing a
 * required field.
 *
 * If attendance details are provided for a non-existent session, the call
 * succeeds for valid sessions while marking the unavailable session as a
 * failure in the response.
 */
export function bulkSetAttendance(payload: object): RequestOptionsFactory<any> {
  function __bulkSetAttendance({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'attendanceList.updatedDate' },
          { path: 'attendanceList.statusUpdatedDate' },
          { path: 'attendanceDetails.attendance.updatedDate' },
          { path: 'attendanceDetails.attendance.statusUpdatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.BulkSetAttendance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/bulk/attendance/set',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.updatedDate' },
              { path: 'results.item.statusUpdatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkSetAttendance;
}

/**
 * Creates a query to retrieve a list of attendances.
 *
 * The `queryAttendances()` function builds a query to retrieve a list of attendances and returns a `AttendancesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to call the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/bookings/attendance/attendances-query-builder/find) function.
 *
 * You can refine the query by chaining `AttendancesQueryBuilder` functions onto the query. `AttendancesQueryBuilder` functions enable you to sort, filter, and control the results that `queryAttendances()` returns.
 *
 * `queryAttendances()` uses the following `AttendancesQueryBuilder` default values that you can override:
 *
 * + `limit` is `50`.
 * + Sorted by `id` in ascending order.
 *
 * The functions that are chained to `queryAttendances()` are applied in the order they are called. For example, if you apply `ascending("status")` and then `ascending("numberOfAttendees")`, the results are sorted first by the `"status"`, and then, if there are multiple results with the same `"status"`, the items are sorted by `"numberOfAttendees"`.
 *
 * The following `AttendancesQueryBuilder` functions are supported for the `queryAttendances()` function. For a full description of the tip settings object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/bookings/attendance/attendances-query-result/items) property in `AttendancesQueryResult`.
 */
export function queryAttendance(payload: object): RequestOptionsFactory<any> {
  function __queryAttendance({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.QueryAttendance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/attendance/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'attendances.updatedDate' },
              { path: 'attendances.statusUpdatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryAttendance;
}

/**
 * Counts attendance records by contact.
 *
 * Returns the total number of attendance records for the contact,
 * with optional filtering by attendance status and service IDs.
 *
 * Filtering options:
 * + **attendance_status**: Filter by attendance status (ATTENDED, NOT_ATTENDED)
 * + **service_id**: Filter by one or more service IDs from the bookings
 *
 * If no filters are provided, returns the total count of all attendance records
 * for the contact.
 */
export function countAttendances(payload: object): RequestOptionsFactory<any> {
  function __countAttendances({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.CountAttendances',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/attendance/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countAttendances;
}

/**
 * Deletes an attendance record.
 *
 *
 * This permanently removes the attendance information for a booking session.
 * To recreate the attendance information, call [Set Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/set-attendance).
 *
 * To delete multiple attendance records in a single call, call
 * [Bulk Delete Attendances](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/bulk-delete-attendances).
 */
export function deleteAttendance(payload: object): RequestOptionsFactory<any> {
  function __deleteAttendance({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.DeleteAttendance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/attendance/{attendanceId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAttendance;
}

/**
 * Deletes multiple attendance records.
 *
 *
 * This permanently removes the attendance information for multiple booking sessions.
 * To recreate the attendance information, call [Set Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/set-attendance) or
 * [Bulk Set Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/bulk-set-attendance).
 *
 * To delete a single attendance record, call [Delete Attendance](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/attendance/delete-attendance).
 */
export function bulkDeleteAttendances(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteAttendances({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.v2.attendance',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.attendance.v2.AttendanceService.BulkDeleteAttendances',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAttendanceV2AttendanceServiceUrl({
        protoPath: '/v2/bulk/attendance/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteAttendances;
}
